/* eslint-disable no-raw-nulls/no-raw-nulls -- Browser refs and MediaRecorder event slots use null at the DOM boundary. */
import React, { useCallback, useMemo, useRef, useState } from "react";
import * as E from "fp-ts/lib/Either";

import { useDocumentAssistantDictationClient } from "@contexts/DocumentAssistantDictationClient.context";
import type { Ok } from "@ps-bridge/ok";
import { ok } from "@ps-bridge/ok";
import {
  decodeDictationAudioCapture,
  dictationError,
  type DictationError,
  type DictationErrorCode,
  type DictationFieldId,
  type DictationRecordingState,
} from "@services/clients/DocumentAssistantDictationApi";
import { evaluateTaskEither } from "@utils/effects/taskEitherRunner";
import { ioEffect } from "@utils/effects/branded";
import { cleanupSome, useIoEffectWithCleanup } from "@hooks/effects/useIoEffectWithCleanup";
import * as Option from "@utils/Option";

type UseDocumentAssistantDictationArgs = {
  readonly fieldId: DictationFieldId;
  readonly value: string;
  readonly setValue: (value: string) => Ok;
};

type DictationInputBindings = {
  readonly ref: React.RefObject<HTMLTextAreaElement>;
  readonly onFocus: () => Ok;
  readonly onClick: () => Ok;
  readonly onSelect: () => Ok;
};

type DictationControlState = {
  readonly fieldId: DictationFieldId;
  readonly state: DictationRecordingState;
  readonly errorMessage: string;
  readonly isBusy: boolean;
  readonly isRecording: boolean;
  readonly onToggle: () => Ok;
};

type TextSelection = {
  readonly start: number;
  readonly end: number;
};

export type UseDocumentAssistantDictationResult = {
  readonly inputBindings: DictationInputBindings;
  readonly controlState: DictationControlState;
};

const preferredMimeTypes = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];

const selectMimeType = (): string => {
  const recorder = window.MediaRecorder;
  if (!recorder || typeof recorder.isTypeSupported !== "function") {
    return "audio/webm";
  }

  return preferredMimeTypes.find((mimeType) => recorder.isTypeSupported(mimeType)) ?? "audio/webm";
};

const extensionFromMimeType = (mimeType: string): string => {
  if (mimeType.includes("mp4")) {
    return "mp4";
  }
  if (mimeType.includes("ogg")) {
    return "ogg";
  }
  return "webm";
};

const insertTranscript = (
  currentValue: string,
  transcript: string,
  selection: TextSelection | null,
): {
  readonly nextValue: string;
  readonly caret: number;
} => {
  if (!selection) {
    const separator = currentValue.trim().length > 0 && !/\s$/u.test(currentValue) ? " " : "";
    const nextValue = `${currentValue}${separator}${transcript}`;
    return { nextValue, caret: nextValue.length };
  }

  const start = selection.start;
  const end = selection.end;
  const prefix = start > 0 && !/\s$/u.test(currentValue.slice(0, start)) && !/^\s/u.test(transcript) ? " " : "";
  const suffix = end < currentValue.length && !/\s$/u.test(transcript) && !/^\s/u.test(currentValue.slice(end)) ? " " : "";
  const insertedTranscript = `${prefix}${transcript}${suffix}`;
  const nextValue = `${currentValue.slice(0, start)}${insertedTranscript}${currentValue.slice(end)}`;
  return { nextValue, caret: start + prefix.length + transcript.length };
};

const localError = (code: DictationErrorCode, message: string, fieldId: DictationFieldId): DictationError =>
  dictationError(code, message, Option.some(fieldId), Option.none());

export const useDocumentAssistantDictation = ({
  fieldId,
  value,
  setValue,
}: UseDocumentAssistantDictationArgs): UseDocumentAssistantDictationResult => {
  const client = useDocumentAssistantDictationClient();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const activeRef = useRef(false);
  const selectionRef = useRef<TextSelection | null>(null);
  const valueRef = useRef(value);
  const [state, setState] = useState<DictationRecordingState>("idle");
  const [error, setError] = useState<DictationError | null>(null);

  valueRef.current = value;

  const readSelection = useCallback((): TextSelection | null => {
    const control = inputRef.current;
    if (!control || typeof control.selectionStart !== "number" || typeof control.selectionEnd !== "number") {
      return null;
    }
    return {
      start: control.selectionStart ?? valueRef.current.length,
      end: control.selectionEnd ?? control.selectionStart ?? valueRef.current.length,
    };
  }, []);

  const markActive = useCallback(() => {
    activeRef.current = true;
    selectionRef.current = readSelection();
    return ok;
  }, [readSelection]);

  const finishStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const transcribeChunks = useCallback(async () => {
    const mimeType = recorderRef.current?.mimeType || selectMimeType();
    const blob = new Blob(chunksRef.current, { type: mimeType });
    const durationMillis = Date.now() - startedAtRef.current;
    recorderRef.current = null;
    finishStream();

    const decodedCapture = decodeDictationAudioCapture({
      blob,
      durationMillis,
      extension: extensionFromMimeType(mimeType),
      fieldId,
      mimeType,
    });

    if (decodedCapture.type === "error") {
      setError(localError("emptyRecording", decodedCapture.message, fieldId));
      setState("failed");
      return;
    }

    setState("transcribing");
    const result = await evaluateTaskEither(client.transcribeAudio({ capture: decodedCapture.value }));
    if (E.isLeft(result)) {
      setError(result.left);
      setState("failed");
      return;
    }

    const control = inputRef.current;
    const { nextValue, caret } = insertTranscript(valueRef.current, result.right.transcript, selectionRef.current);
    setValue(nextValue);
    setError(null);
    setState("idle");

    window.requestAnimationFrame(() => {
      control?.focus();
      control?.setSelectionRange(caret, caret);
      return ok;
    });
  }, [client, fieldId, finishStream, setValue]);

  const startRecording = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      setError(localError("unsupportedBrowser", "Dictation recording is not supported by this browser.", fieldId));
      setState("failed");
      return;
    }

    try {
      setError(null);
      chunksRef.current = [];
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = selectMimeType();
      const recorder = new MediaRecorder(stream, { mimeType });
      streamRef.current = stream;
      recorderRef.current = recorder;
      startedAtRef.current = Date.now();
      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
        return ok;
      };
      recorder.onstop = () => {
        transcribeChunks().then(() => ok);
        return ok;
      };
      recorder.onerror = () => {
        finishStream();
        setError(localError("uploadFailed", "Dictation recording stopped unexpectedly.", fieldId));
        setState("failed");
        return ok;
      };
      recorder.start();
      setState("recording");
    } catch {
      finishStream();
      setError(localError("permissionDenied", "Allow microphone access to use dictation.", fieldId));
      setState("failed");
    }
  }, [fieldId, finishStream, transcribeChunks]);

  const stopRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === "inactive") {
      return;
    }
    setState("stopping");
    recorder.stop();
  }, []);

  const toggle = useCallback(() => {
    if (state === "recording") {
      stopRecording();
      return ok;
    }
    if (state === "idle" || state === "failed") {
      selectionRef.current = activeRef.current ? readSelection() : null;
      activeRef.current = true;
      startRecording().then(() => ok);
    }
    return ok;
  }, [readSelection, startRecording, state, stopRecording]);

  const keyboardSetup = useMemo(
    () =>
      ioEffect(() => {
        const onKeyDown = (event: KeyboardEvent): Ok => {
      if (!event.ctrlKey || !event.shiftKey || event.key.toLowerCase() !== "d") {
        return ok;
      }
      if (!activeRef.current) {
        return ok;
      }
      event.preventDefault();
      toggle();
      return ok;
        };

        window.addEventListener("keydown", onKeyDown);
        return cleanupSome(
          ioEffect(() => {
            window.removeEventListener("keydown", onKeyDown);
            return ok;
          }),
        );
      }),
    [toggle],
  );
  useIoEffectWithCleanup(keyboardSetup, [keyboardSetup]);

  const recorderCleanupSetup = useMemo(
    () =>
      ioEffect(() =>
        cleanupSome(
          ioEffect(() => {
            if (recorderRef.current?.state === "recording") {
              recorderRef.current.stop();
            }
            finishStream();
            return ok;
          }),
        ),
      ),
    [finishStream],
  );
  useIoEffectWithCleanup(recorderCleanupSetup, [recorderCleanupSetup]);

  const isRecording = state === "recording" || state === "stopping";
  const isBusy = isRecording || state === "transcribing";

  return {
    inputBindings: {
      ref: inputRef,
      onFocus: markActive,
      onClick: markActive,
      onSelect: markActive,
    },
    controlState: {
      fieldId,
      state,
      errorMessage: error?.message ?? "",
      isBusy,
      isRecording,
      onToggle: toggle,
    },
  };
};
